import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { SITE_BG, SURFACE } from "../../../dark-theme/palette";
import Item from "../../components/Item";
import CrossCard from "../../puzzles/crossword/components/CrossCard";
import { decryptText } from "../../../services/CryptoUtil";
import Logger from "../../../services/Logger";
import encryptedSampleDataEn from "../puzzle-crossword/samples/sample_en_cross_encrypted.json";
import encryptedSampleDataFr from "../puzzle-crossword/samples/sample_fr_cross_encrypted.json";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import { SUPPORT_FRENCH } from "../../../config/supportFrench";
import { usePagesTranslation } from "../../locales";

const extractEncryptedText = (data) => {
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data.join("") || data[0];
    if (typeof data === "object" && data !== null) {
        return data.data || data.encrypted || data.text || data.default || Object.values(data)[0];
    }
    return null;
};

/**
 * Explains the disabled puzzle state: why puzzles are disabled, how they are shown,
 * and when they are automatically removed.
 */
export default function HowToDisabledPuzzle() {
    const { t } = usePagesTranslation();
    const [samplePuzzle, setSamplePuzzle] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState("english");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setSamplePuzzle(null);
        const loadAndDecryptData = () => {
            try {
                const encryptedSampleData = selectedLanguage === "french" ? encryptedSampleDataFr : encryptedSampleDataEn;
                if (!encryptedSampleData) {
                    if (!cancelled) setSamplePuzzle(null);
                    return;
                }
                const encryptedText = extractEncryptedText(encryptedSampleData);
                if (!encryptedText || typeof encryptedText !== "string") {
                    if (!cancelled) setSamplePuzzle(null);
                    return;
                }
                const decryptedText = decryptText(encryptedText.trim());
                if (decryptedText && !cancelled) {
                    const parsedData = JSON.parse(decryptedText);
                    const raw = Array.isArray(parsedData) && parsedData.length > 0 ? parsedData[0] : (typeof parsedData === "object" ? parsedData : null);
                    if (raw) {
                        const disabledAt = Date.now() - 24 * 60 * 60 * 1000;
                        setSamplePuzzle({
                            ...raw,
                            state: "DISABLED",
                            disabledAt
                        });
                    } else {
                        setSamplePuzzle(null);
                    }
                } else if (!cancelled) {
                    setSamplePuzzle(null);
                }
            } catch (error) {
                Logger.error("Error loading sample for disabled puzzle:", error);
                if (!cancelled) setSamplePuzzle(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadAndDecryptData();
        return () => { cancelled = true; };
    }, [selectedLanguage]);

    const handleLanguageChange = (event, newLanguage) => {
        if (newLanguage !== null) setSelectedLanguage(newLanguage);
    };
    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Item sx={{ p: { xs: 2, md: 4 }, textAlign: "left", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                    {/* Title */}
                    <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 600, color: "#1976d2" }}>
                        {t("howto.disabled.title")}
                    </Typography>

                    {/* Introduction */}
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.disabled.what-title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.disabled.what-p")}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* How It Looks */}
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.disabled.how-title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.disabled.how-p")}
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.disabled.how-title-grey")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.disabled.how-warning")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.disabled.how-btn")}</Typography></li>
                        </Box>

                        <Typography variant="body1" sx={{ mb: 2, fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.disabled.sample-intro")}
                        </Typography>
                        {SUPPORT_FRENCH ? (
                        <Box sx={{ mb: 2 }}>
                            <ToggleButtonGroup
                                value={selectedLanguage}
                                exclusive
                                onChange={handleLanguageChange}
                                aria-label={t("howto.sample-aria")}
                                sx={{
                                    "& .MuiToggleButton-root": {
                                        "&.Mui-selected": {
                                            borderColor: "#3BA666 !important",
                                            borderWidth: "2px !important",
                                            backgroundColor: "rgba(59, 166, 102, 0.08)",
                                            "&:hover": {
                                                backgroundColor: "rgba(59, 166, 102, 0.12)"
                                            }
                                        }
                                    }
                                }}
                            >
                                <Tooltip
                                    title={t("howto.tooltip-english")}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                        tooltip: { sx: { fontSize: "0.9rem", padding: "8px 12px", backgroundColor: "#3BA666", fontWeight: 500, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" } },
                                        arrow: { sx: { color: "#3BA666" } }
                                    }}
                                >
                                    <ToggleButton value="english" aria-label="english">
                                        {t("howto.english-example")}
                                    </ToggleButton>
                                </Tooltip>
                                <Tooltip
                                    title={t("howto.tooltip-french")}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                        tooltip: { sx: { fontSize: "0.9rem", padding: "8px 12px", backgroundColor: "#3BA666", fontWeight: 500, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" } },
                                        arrow: { sx: { color: "#3BA666" } }
                                    }}
                                >
                                    <ToggleButton value="french" aria-label="french">
                                        {t("howto.french-example")}
                                    </ToggleButton>
                                </Tooltip>
                            </ToggleButtonGroup>
                        </Box>
                        ) : null}
                        {loading ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t("howto.loading-sample")}</Typography>
                        ) : samplePuzzle ? (
                            <Box sx={{ maxWidth: 360 }}>
                                <CrossCard
                                    puzzle={samplePuzzle}
                                    userPayPlan="STANDARD"
                                    buttonText={t("howto.replay")}
                                    forceUnlock={true}
                                    onPlayClick={() => {}}
                                    hideClusteredBubbles
                                />
                            </Box>
                        ) : null}
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.disabled.auto-title")}
                        </Typography>
                        <Typography variant="body1" component="div" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }} dangerouslySetInnerHTML={{ __html: t("howto.disabled.auto-p") }} />
                        <Paper sx={{ p: 2, backgroundColor: SURFACE.sunken, mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("howto.disabled.summary")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.disabled.summary-p")}
                            </Typography>
                        </Paper>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.disabled.applies-to")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.disabled.applies-p")}
                        </Typography>
                    </Box>
                </Item>
            </Box>
        </Box>
    );
}
